import dayjs from 'dayjs';

import { Language } from '../modules';

export function wait(milliseconds: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

// True when the app is running against the production environment (VITE_STAGE).
export function isProduction(): boolean {
  return import.meta.env.VITE_STAGE === 'production';
}

export function createBase64(data: Blob): Promise<ArrayBuffer | null | string> {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.addEventListener('loadend', () => {
      resolve(reader.result);
    });

    reader.readAsDataURL(data);
  });
}

export function formatDate(
  date: Date | string,
  opts: { format?: string; incomingFormat?: string } = {
    format: 'YYYY-MM-DD',
    incomingFormat: 'YYYYMMDD',
  },
): string {
  return dayjs.utc(date, opts.incomingFormat).format(opts.format);
}

/** The wire format the tilers expect for a `datetime` query parameter. */
const TILER_DATETIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss[Z]';

/**
 * A layer's date as a tiler `datetime` value, or `undefined` when it has none.
 *
 * `formatDate` runs the input through dayjs and **stringifies failure**: given
 * `''`, `null` or `undefined` it returns the literal `"Invalid Date"`, not an
 * empty string. Interpolated straight into a query parameter that reads as
 * absent but is not — tipg tests `if datetime:`, so `"Invalid Date"` is truthy
 * and a dateless collection answers `MissingDatetimeColumn`, surfacing as an
 * opaque HTTP 500.
 *
 * Every caller that puts a layer date on a tiler URL goes through here, so the
 * check cannot be present on one path and missing on the next — which is what
 * happened: the `/items` reader guarded it, the vector-tile URL did not.
 */
export function tilerDatetimeParam(date: string | null | undefined): string | undefined {
  if (!date) {
    return undefined;
  }
  const formatted = formatDate(date, { format: TILER_DATETIME_FORMAT });
  return formatted && formatted !== 'Invalid Date' ? formatted : undefined;
}

/** Filename-safe slug: lowercase, runs of non-alphanumerics → single '-', trimmed; `fallback` when empty. */
export function slugify(value: string, fallback = 'aoi'): string {
  return (
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '') || fallback
  );
}

export function getMetaTitle(title: string | null = null): string {
  if (!title) {
    return 'NatureOS Insights';
  }

  return `${title} - NatureOS Insights`;
}

export function ucfirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/** Title-case a snake_case column key for display (`pods_total` → `Pods total`). */
export function humanizeColumnKey(key: string): string {
  const base = key.endsWith('_hist') ? key.slice(0, -5) : key;
  const words = base.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function formatNumber(value: string | number, locale: string = Language.EN_US): string {
  let numberValue: number;

  if (typeof value === 'string') {
    numberValue = parseFloat(value);
  } else {
    numberValue = value;
  }

  if (!Number.isFinite(numberValue)) {
    return 'n/a';
  }

  return new Intl.NumberFormat(locale, {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numberValue);
}
